import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../../services/api';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useConfirm } from '../../../contexts/ConfirmContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkingMode } from '../../../contexts/WorkingModeContext';
import EmptyState from '../../UI/EmptyState';
import SidePanel from '../../UI/SidePanel';
import styles from '../../../styles/TaskDetails.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import sidePanelStyles from '../../../styles/SidePanel.module.css';
import SkeletonLoader from '../../UI/SkeletonLoader';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  username: string;
  email: string;
  full_name?: string;
}

interface TaskCommentAttachment {
  id: string;
  filename: string;
  file_size: number;
  file_type: string;
  file_size_display: string;
  url: string;
}

interface TaskCommentReaction {
  count: number;
  users: {
    first_name: string;
    last_name: string;
    username: string;
    full_name: string;
  }[];
}

interface TaskComment {
  id: string;
  content: string;
  action_type: string;
  attachments: TaskCommentAttachment[];
  reactions: { [emoji: string]: TaskCommentReaction };
  replies?: TaskComment[];
  parent_comment?: string;
  created_by: User;
  created_at: string;
}

interface GroupInfo {
  id: string;
  name: string;
  description: string;
}

interface TaskDetailsData {
  id: string;
  name: string;
  description: string;
  status: string;
  requires_documents: boolean;
  created_by: User;
  created_at: string;
  due_date: string | null;
  comments: TaskComment[];
  group: GroupInfo | null;
  approvers?: Array<{
    id: string;
    user: User;
  }>;
  approver_ids?: string[];
  requires_approval?: boolean;
  approval_type?: string;
}

interface ProofOfWorkSubmission {
  id: string;
  submission_number: number;
  status: 'submitted' | 'under_review' | 'approved' | 'rejected' | 'resubmitted';
  submission_notes: string;
  submitted_at: string;
  reviewed_at?: string;
  reviewed_by?: User;
  review_notes?: string;
  rejection_reason?: string;
  rejection_details?: string;
  approved_at?: string;
  approved_by?: User;
  submitted_by: User;
  documents: any[];
  assignment: {
    id: string;
    assignee: {
      user: User;
    };
  };
}

interface TaskUpdatesProps {
  currentUserAssignment?: {
    status: 'pending' | 'in_progress' | 'completed';
  };
  task?: TaskDetailsData;
  onTaskUpdate?: () => void;
}

const TaskUpdates: React.FC<TaskUpdatesProps> = ({ currentUserAssignment, task: taskProp, onTaskUpdate }) => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const confirmService = useConfirm();
  const { user: currentUser } = useAuth();
  const { isWorkMode } = useWorkingMode();
  const isPersonalMode = !isWorkMode;

  const [task, setTask] = useState<TaskDetailsData | null>(taskProp || null);
  const [loading, setLoading] = useState(!taskProp);
  const [error, setError] = useState<any>(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(!!taskProp);
  const [hasFailedToLoad, setHasFailedToLoad] = useState(false);
  const isLoadingRef = useRef(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!taskProp);

  useEffect(() => {
    if (taskProp) {
      setTask(taskProp);
      setLoading(false);
      setHasInitiallyLoaded(true);
      setHasFailedToLoad(false);
      setIsInitialLoad(false);
    }
  }, [taskProp]);

  const loadTaskDetails = useCallback(async () => {
    if (!taskId || isLoadingRef.current || taskProp) return;

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      // Pass filter parameters based on mode: 
      // In personal mode, don't filter comments on backend - we'll filter on frontend
      // based on whether user is an approver (approvers see all comments)
      const params = {
        filter_comments_by_user: false, // Load all comments, filter on frontend
        work_mode: (isPersonalMode ? 'personal' : 'work') as 'personal' | 'work'
      };

      const response = await apiService.getTaskDetails(taskId, params);

      if (response.status === 'success') {
        setTask(response.data);
        setHasInitiallyLoaded(true);
        setHasFailedToLoad(false);
        setIsInitialLoad(false);
      } else {
        setError(response.message || 'Failed to load task details');
        setHasFailedToLoad(true);
        notificationService.error(response.message || 'Failed to load task details');
      }
    } catch (err: any) {
      console.error('Error loading task details:', err);
      setError(err);
      setHasFailedToLoad(true);
      setIsInitialLoad(false);
      notificationService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load task details');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [taskId, taskProp, notificationService, isPersonalMode]);

  useEffect(() => {
    if (!taskProp) {
      loadTaskDetails();
    }
  }, [loadTaskDetails, taskProp]);

  const refetchTaskDetails = useCallback(() => {
    if (onTaskUpdate) {
      // If we have a parent callback, use it to refresh parent data
      onTaskUpdate();
    } else if (!taskProp) {
      // Only load locally if we don't have task prop (standalone usage)
      loadTaskDetails();
    }
  }, [loadTaskDetails, taskProp, onTaskUpdate]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedUpdateAction, setSelectedUpdateAction] = useState('progress');
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [markingAsDone, setMarkingAsDone] = useState(false);
  const [startingProgress, setStartingProgress] = useState(false);
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  const [pendingCompletion, setPendingCompletion] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [submittingReply, setSubmittingReply] = useState<Record<string, boolean>>({});
  const [visibleReplies, setVisibleReplies] = useState<Set<string>>(new Set());
  const [groupId, setGroupId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const actionDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const [submissionNote, setSubmissionNote] = useState('');
  const [showAddUpdatePanel, setShowAddUpdatePanel] = useState(false);

  // User submissions state for submit button logic
  const [userSubmissions, setUserSubmissions] = useState<ProofOfWorkSubmission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  // Determine if user is admin based on role
  const isAdmin = currentUser?.role === 'admin';

  // Extract groupId from URL path or task
  useEffect(() => {
    // Try to get groupId from URL path
    const pathSegments = window.location.pathname.split('/');
    const groupManagementIndex = pathSegments.indexOf('groups');
    if (groupManagementIndex !== -1 && pathSegments[groupManagementIndex + 1]) {
      setGroupId(pathSegments[groupManagementIndex + 1]);
      return;
    }

    // If not in URL, get from task
    if (task?.group?.id) {
      setGroupId(task.group.id);
    }
  }, [task]);

  // Load user submissions to check if submit button should be shown
  const loadUserSubmissions = useCallback(async () => {
    if (!taskId || !groupId || !currentUser?.id) return;

    try {
      setLoadingSubmissions(true);
      const response = await apiService.getUserSubmissions(taskId, groupId, currentUser.id);

      if (response.status === 'success') {
        setUserSubmissions(response.data || []);
      } else {
        console.error('Failed to load user submissions:', response.message);
      }
    } catch (error: any) {
      console.error('Error loading user submissions:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load user submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  }, [taskId, groupId, currentUser?.id]);

  useEffect(() => {
    if (taskId && groupId && currentUser?.id && !isAdmin) {
      loadUserSubmissions();
    }
  }, [taskId, groupId, currentUser?.id, isAdmin, loadUserSubmissions]);
  const shouldShowSubmitButton = () => {

    if (userSubmissions.length === 0) {
      return true;
    }

    const latestSubmission = userSubmissions[0];
    if (latestSubmission.status === 'rejected') {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target as Node)) {
        setShowActionDropdown(false);
      }
    };

    if (showActionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target as Node)) {
        setShowReactionPicker(null);
      }
    };

    if (showReactionPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReactionPicker]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !taskId || !groupId) return;

    setSubmittingComment(true);
    try {
      const response = await apiService.addTaskComment(taskId, groupId, {
        content: newComment,
        action_type: selectedUpdateAction,
        attachments: selectedFiles
      });

      if (response.status === 'success') {
        notificationService.success('Update added successfully');
        setNewComment('');
        setSelectedUpdateAction('progress');
        setSelectedFiles([]);
        setShowActionDropdown(false);
        setShowAddUpdatePanel(false);

        refetchTaskDetails();
      } else {
        notificationService.error(response.message || 'Failed to add update');
      }
    } catch (error: any) {
      console.error('Error adding comment:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to add update');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getFileIcon = (fileType: string, filename?: string) => {
    const lowerFileType = fileType.toLowerCase();
    const lowerFilename = filename?.toLowerCase() || '';

    // Check for suspicious file extensions first
    const suspiciousExtensions = ['.php.pdf', '.exe.pdf', '.scr.pdf', '.bat.pdf', '.cmd.pdf'];
    const isSuspicious = suspiciousExtensions.some(ext => lowerFilename.includes(ext));

    if (isSuspicious) {
      return (
        <svg width="16" height="16" className={styles.suspiciousFileIcon}>
          <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8" />
          <path d="M9.653 5.496A3 3 0 0 0 8 5c-.61 0-1.179.183-1.653.496L4.694 2.992A5.97 5.97 0 0 1 8 2c1.222 0 2.358.365 3.306.992zm1.342 2.324a3 3 0 0 1-.884 2.312 3 3 0 0 1-.769.552l1.342 2.683c.57-.286 1.09-.66 1.538-1.103a6 6 0 0 0 1.767-4.624zm-5.679 5.548 1.342-2.684A3 3 0 0 1 5.005 7.82l-2.994-.18a6 6 0 0 0 3.306 5.728ZM10 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0" />
        </svg>
      );
    }

    if (lowerFileType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
      return (
        <svg width="16" height="16" fill="#ef4444" className={styles.fileIcon}>
          <path d="M5.523 10.424q.21-.124.459-.238a8 8 0 0 1-.45.606c-.28.337-.498.516-.635.572l-.035.012a.3.3 0 0 1-.026-.044c-.056-.11-.054-.216.04-.36.106-.165.319-.354.647-.548m2.455-1.647q-.178.037-.356.078a21 21 0 0 0 .5-1.05 12 12 0 0 0 .51.858q-.326.048-.654.114m2.525.939a4 4 0 0 1-.435-.41q.344.007.612.054c.317.057.466.147.518.209a.1.1 0 0 1 .026.064.44.44 0 0 1-.06.2.3.3 0 0 1-.094.124.1.1 0 0 1-.069.015c-.09-.003-.258-.066-.498-.256M8.278 4.97c-.04.244-.108.524-.2.829a5 5 0 0 1-.089-.346c-.076-.353-.087-.63-.046-.822.038-.177.11-.248.196-.283a.5.5 0 0 1 .145-.04c.013.03.028.092.032.198q.008.183-.038.465z" />
          <path fill-rule="evenodd" d="M4 0h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2m.165 11.668c.09.18.23.343.438.419.207.075.412.04.58-.03.318-.13.635-.436.926-.786.333-.401.683-.927 1.021-1.51a11.6 11.6 0 0 1 1.997-.406c.3.383.61.713.91.95.28.22.603.403.934.417a.86.86 0 0 0 .51-.138c.155-.101.27-.247.354-.416.09-.181.145-.37.138-.563a.84.84 0 0 0-.2-.518c-.226-.27-.596-.4-.96-.465a5.8 5.8 0 0 0-1.335-.05 11 11 0 0 1-.98-1.686c.25-.66.437-1.284.52-1.794.036-.218.055-.426.048-.614a1.24 1.24 0 0 0-.127-.538.7.7 0 0 0-.477-.365c-.202-.043-.41 0-.601.077-.377.15-.576.47-.651.823-.073.34-.04.736.046 1.136.088.406.238.848.43 1.295a20 20 0 0 1-1.062 2.227 7.7 7.7 0 0 0-1.482.645c-.37.22-.699.48-.897.787-.21.326-.275.714-.08 1.103" />
        </svg>
      );
    } else if (lowerFileType.includes('excel') || lowerFileType.includes('spreadsheet') || lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls')) {
      return (
        <svg width="16" height="16" fill="#10b981" className={styles.fileIcon}>
          <path d="M6 12v-2h3v2z" />
          <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M3 9h10v1h-3v2h3v1h-3v2H9v-2H6v2H5v-2H3v-1h2v-2H3z" />
        </svg>
      );
    } else if (lowerFileType.includes('word') || lowerFileType.includes('document') || lowerFilename.endsWith('.docx') || lowerFilename.endsWith('.doc')) {
      return (
        <svg width="16" height="16" fill="#3b82f6" className={styles.fileIcon}>
          <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2M5.485 4.879l1.036 4.144.997-3.655a.5.5 0 0 1 .964 0l.997 3.655 1.036-4.144a.5.5 0 0 1 .97.242l-1.5 6a.5.5 0 0 1-.967.01L8 7.402l-1.018 3.73a.5.5 0 0 1-.967-.01l-1.5-6a.5.5 0 1 1 .97-.242z" />
        </svg>
      );
    } else if (lowerFileType.includes('image') || lowerFilename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
      return (
        <svg width="16" height="16" fill="#8b5cf6" className={styles.fileIcon}>
          <path d="M8.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
          <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2M3 2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v8l-2.083-2.083a.5.5 0 0 0-.76.063L8 11 5.835 9.7a.5.5 0 0 0-.611.076L3 12z" />
        </svg>
      );
    } else if (lowerFileType.includes('zip') || lowerFileType.includes('archive') || lowerFilename.match(/\.(zip|rar|7z|tar|gz)$/)) {
      return (
        <svg width="16" height="16" className={styles.fileIcon}>
          <rect x="3" y="3" width="18" height="18" rx="2" fill="#f59e0b" stroke="#d97706" strokeWidth="1" />
          <path d="M8 3L8 7L12 7" stroke="white" strokeWidth="1.5" fill="none" />
          <path d="M8 10L16 10" stroke="white" strokeWidth="1.5" />
          <path d="M8 13L16 13" stroke="white" strokeWidth="1.5" />
          <path d="M8 16L16 16" stroke="white" strokeWidth="1.5" />
        </svg>
      );
    } else {
      return (
        <svg width="16" height="16" fill="#6b7280" className={styles.fileIcon}>
          <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1" />
        </svg>
      );
    }
  };

  const handleReaction = async (commentId: string, emoji: string) => {
    console.log('handleReaction called:', { taskId, groupId, commentId, emoji, isAdmin });

    if (!taskId) {
      console.error('Missing taskId');
      return;
    }

    if (!groupId) {
      console.error('Missing groupId - this is the problem!');
      console.log('Current state:', { task, groupId, isAdmin });
      return;
    }

    // Store original task state for rollback
    const originalTask = task ? JSON.parse(JSON.stringify(task)) : null;

    // Optimistic update: Add reaction to UI immediately
    if (task && originalTask && currentUser) {
      setTask(prevTask => {
        if (!prevTask) return prevTask;
        const updatedComments = prevTask.comments.map(comment => {
          if (comment.id === commentId) {
            const currentReactions = comment.reactions || {};
            const currentReaction = currentReactions[emoji] || { count: 0, users: [] };

            // Check if user already reacted with this emoji
            const userAlreadyReacted = currentReaction.users.some(
              user => user.username === currentUser.username
            );

            let updatedReaction;
            if (userAlreadyReacted) {
              // Remove reaction if user already reacted
              updatedReaction = {
                count: Math.max(0, currentReaction.count - 1),
                users: currentReaction.users.filter(user => user.username !== currentUser.username)
              };
            } else {
              // Add reaction
              updatedReaction = {
                count: currentReaction.count + 1,
                users: [
                  ...currentReaction.users,
                  {
                    first_name: currentUser.first_name || '',
                    last_name: currentUser.last_name || '',
                    username: currentUser.username || '',
                    full_name: (currentUser as any).full_name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
                  }
                ]
              };
            }

            const updatedReactions = { ...currentReactions };
            if (updatedReaction.count > 0) {
              updatedReactions[emoji] = updatedReaction as TaskCommentReaction;
            } else {
              delete updatedReactions[emoji];
            }

            return {
              ...comment,
              reactions: updatedReactions
            };
          }
          return comment;
        });
        return {
          ...prevTask,
          comments: updatedComments
        };
      });
    }

    // Close reaction picker
    setShowReactionPicker(null);

    try {
      console.log('Making API call for reaction...');
      const response = await apiService.addTaskCommentReaction(taskId, groupId, commentId, emoji);

      if (response.status === 'success') {
        console.log('Reaction added successfully:', response.data);
        // Reload task details to get updated reaction counts from server
        refetchTaskDetails();
      } else {
        console.error('API returned error:', response.message);
        // Rollback on failure
        if (originalTask) {
          setTask(originalTask);
        }
        notificationService.error(response.message || 'Failed to add reaction');
      }
    } catch (error: any) {
      console.error('Error adding reaction:', error);
      // Rollback on error
      if (originalTask) {
        setTask(originalTask);
      }
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to add reaction');
    }
  };

  const getAvailableEmojis = () => {
    return ['👍', '❤️', '😂', '😮', '😢', '😡', '✅', '🎉', '👏', '🔥'];
  };

  const handleReply = async (commentId: string) => {
    const text = replyText[commentId] || '';
    if (!text.trim() || !taskId || !groupId || submittingReply[commentId]) return;

    // Set loading state for this comment
    setSubmittingReply(prev => ({ ...prev, [commentId]: true }));

    // Optimistic update: Add reply to UI immediately
    if (!currentUser) return;

    const optimisticReply: TaskComment = {
      id: `temp-${Date.now()}-${Math.random()}`,
      content: text,
      action_type: 'general',
      attachments: [],
      reactions: {},
      created_by: {
        id: currentUser.id,
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        name: (currentUser as any).name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim(),
        username: currentUser.username || '',
        email: currentUser.email || '',
        full_name: (currentUser as any).full_name || `${currentUser.first_name} ${currentUser.last_name}`.trim()
      },
      created_at: new Date().toISOString(),
      parent_comment: commentId
    };

    // Store original task state for rollback
    const originalTask = task ? JSON.parse(JSON.stringify(task)) : null;

    // Optimistically add reply to the comment
    if (task && originalTask) {
      setTask(prevTask => {
        if (!prevTask) return prevTask;
        const updatedComments = prevTask.comments.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), optimisticReply]
            };
          }
          return comment;
        });
        return {
          ...prevTask,
          comments: updatedComments
        };
      });
    }

    // Clear reply text immediately
    setReplyText(prev => {
      const newState = { ...prev };
      delete newState[commentId];
      return newState;
    });

    // Reset textarea height
    const textarea = document.querySelector(`[data-comment-id="${commentId}"] textarea`) as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
    }

    try {
      const response = await apiService.addTaskComment(taskId, groupId, {
        content: text,
        action_type: 'general',
        attachments: [],
        parent_comment: commentId
      });

      if (response.status === 'success') {
        notificationService.success('Reply added successfully');
        // Reload task details to get the actual comment from server
        refetchTaskDetails();
      } else {
        // Rollback on failure
        if (originalTask) {
          setTask(originalTask);
        }
        notificationService.error(response.message || 'Failed to add reply');
      }
    } catch (error: any) {
      console.error('Error adding reply:', error);
      // Rollback on error
      if (originalTask) {
        setTask(originalTask);
      }
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to add reply');
    } finally {
      // Clear loading state for this comment
      setSubmittingReply(prev => {
        const newState = { ...prev };
        delete newState[commentId];
        return newState;
      });
    }
  };

  const toggleReplies = (commentId: string) => {
    setVisibleReplies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!taskId || !groupId) return;

    // Use confirm service instead of window.confirm
    const confirmed = await confirmService.show(
      'Delete Comment',
      'Are you sure you want to delete this comment? This action cannot be undone.',
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await apiService.deleteTaskComment(taskId, groupId, commentId);

      if (response.status === 'success') {
        notificationService.success('Comment deleted successfully');
        // Reload task details to get updated comments
        refetchTaskDetails();
      } else {
        notificationService.error(response.message || 'Failed to delete comment');
      }
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to delete comment');
    }
  };

  const canDeleteComment = (comment: TaskComment) => {
    return currentUser && currentUser.id === comment.created_by.id;
  };

  const getActionTypeBadge = (actionType: string) => {
    const badgeConfig = {
      'progress': {
        text: 'Progress Update',
        color: '#3b82f6',
        bgColor: '#dbeafe',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        )
      },
      'completion': {
        text: 'Completion',
        color: '#10b981',
        bgColor: '#d1fae5',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )
      },
      'approval': {
        text: 'Approval Request',
        color: '#f59e0b',
        bgColor: '#fef3c7',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4"></path>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
        )
      },
      'issue': {
        text: 'Issue/Blocked',
        color: '#ef4444',
        bgColor: '#fee2e2',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        )
      },
      'question': {
        text: 'Question',
        color: '#8b5cf6',
        bgColor: '#ede9fe',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        )
      },
      'feedback': {
        text: 'Feedback',
        color: '#06b6d4',
        bgColor: '#cffafe',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            <path d="M13 8H7"></path>
            <path d="M17 12H7"></path>
          </svg>
        )
      },
      'general': {
        text: 'General Update',
        color: '#6b7280',
        bgColor: '#f3f4f6',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
        )
      }
    };

    return badgeConfig[actionType as keyof typeof badgeConfig] || badgeConfig.general;
  };

  const ReactionItem: React.FC<{ emoji: string; count: number; userNames: string[] }> = ({ emoji, count, userNames }) => {
    const [showUsers, setShowUsers] = useState(false);

    const popupStyle: React.CSSProperties = {
      position: 'absolute',
      left: 'calc(100% + 12px)',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'linear-gradient(135deg, #1f2937 0%, #2d3748 100%)',
      color: '#ffffff',
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      fontSize: '0.75rem',
      fontWeight: 500,
      minWidth: '200px',
      maxWidth: '300px',
      zIndex: 10000,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      pointerEvents: 'none',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      animation: 'fadeIn 0.2s ease-out',
    };

    const headerStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem',
      paddingBottom: '0.5rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    };

    const emojiStyle: React.CSSProperties = {
      fontSize: '1.1rem',
      display: 'inline-block',
      lineHeight: 1,
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
    };

    const countStyle: React.CSSProperties = {
      fontSize: '0.7rem',
      color: 'rgba(255, 255, 255, 0.85)',
      fontWeight: 400,
      display: 'inline-block',
      lineHeight: 1,
    };

    const namesStyle: React.CSSProperties = {
      lineHeight: 1.6,
      wordWrap: 'break-word',
      color: '#ffffff',
      fontSize: '0.75rem',
      margin: 0,
      padding: 0,
    };

    const nameStyle: React.CSSProperties = {
      color: '#ffffff',
      fontWeight: 400,
      display: 'inline',
      lineHeight: 1.6,
    };

    return (
      <div
        className={styles.reactionItem}
        onMouseEnter={() => setShowUsers(true)}
        onMouseLeave={() => setShowUsers(false)}
        style={{ position: 'relative' }}
      >
        <span className={styles.reactionEmoji}>{emoji}</span>
        <span className={styles.reactionCount}>{count}</span>
        {showUsers && userNames.length > 0 && (
          <div style={popupStyle}>
            <div style={headerStyle}>
              <span style={emojiStyle}>{emoji}</span>
              <span style={countStyle}>{count} {count === 1 ? 'reaction' : 'reactions'}</span>
            </div>
            <div style={namesStyle}>
              {userNames.map((name, idx) => (
                <span key={idx} style={nameStyle}>
                  {name}{idx < userNames.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
            <div style={{
              position: 'absolute',
              right: '100%',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              border: '6px solid transparent',
              borderRightColor: '#1f2937',
            }} />
          </div>
        )}
      </div>
    );
  };

  const renderComment = (comment: TaskComment, isReply: boolean = false) => {
    console.log('renderComment called with:', comment, 'isReply:', isReply);
    return (
      <div key={comment.id} className={`${styles.updateItem} ${isReply ? styles.replyItem : ''}`}>
        <div className={styles.updateIcon}>
          <div className={styles.updateAvatar}>
            {comment.created_by.first_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>
        {/* Reactions Display */}

        <div className={styles.updateContent}>
          <div className={styles.updateHeader}>
            <div className={styles.updateAuthor}>
              {comment.created_by.full_name || `${comment.created_by.first_name} ${comment.created_by.last_name}`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div className={styles.actionBadge} style={{
                backgroundColor: getActionTypeBadge(comment.action_type).bgColor,
                color: getActionTypeBadge(comment.action_type).color,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                  {getActionTypeBadge(comment.action_type).icon}
                </span>
                {getActionTypeBadge(comment.action_type).text}
              </div>
              {canDeleteComment(comment) && (
                <button
                  className={styles.deleteButton}
                  onClick={() => handleDeleteComment(comment.id)}
                  title="Delete comment"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              )}
            </div>

          </div>
          <div className={styles.updateText}>
            {comment.content}
          </div>



          <div className={styles.updateActions}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className={styles.reactionButton}
                onClick={() => setShowReactionPicker(showReactionPicker === comment.id ? null : comment.id)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-emoji-laughing" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                  <path d="M12.331 9.5a1 1 0 0 1 0 1A5 5 0 0 1 8 13a5 5 0 0 1-4.33-2.5A1 1 0 0 1 4.535 9h6.93a1 1 0 0 1 .866.5M7 6.5c0 .828-.448 0-1 0s-1 .828-1 0S5.448 5 6 5s1 .672 1 1.5m4 0c0 .828-.448 0-1 0s-1 .828-1 0S9.448 5 10 5s1 .672 1 1.5" />
                </svg>
                Like
              </button>
              {comment.reactions && Object.keys(comment.reactions).length > 0 && (
                <div className={styles.reactionsContainer}>
                  {Object.entries(comment.reactions).map(([emoji, reaction]) => {
                    const userNames = reaction.users.map(user => user.full_name || `${user.first_name} ${user.last_name}`);
                    return (
                      <ReactionItem
                        key={emoji}
                        emoji={emoji}
                        count={reaction.count}
                        userNames={userNames}
                      />
                    );
                  })}
                </div>
              )}


            </div>

            {!isReply && comment.replies && comment.replies.length > 0 && (
              <button
                className={styles.replyButton}
                onClick={() => toggleReplies(comment.id)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Comments ({comment.replies.length})
              </button>
            )}

            {/* Reaction Picker */}
            {showReactionPicker === comment.id && (
              <div className={styles.reactionPicker} ref={reactionPickerRef}>
                {getAvailableEmojis().map(emoji => (
                  <button
                    key={emoji}
                    className={styles.emojiButton}
                    onClick={() => handleReaction(comment.id, emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Reply Form - Only visible for parent comments, not replies */}
          {!isReply && (
            <div className={styles.replyForm} data-comment-id={comment.id}>
              <div className={`${styles.textareaContainer} ${(replyText[comment.id] || '').trim() ? styles.hasText : ''}`}>
                <textarea
                  className={`${styles.replyTextarea} ${(replyText[comment.id] || '').trim() ? styles.hasText : ''}`}
                  placeholder="Add a comment..."
                  rows={1}
                  value={replyText[comment.id] || ''}
                  onChange={(e) => {
                    setReplyText(prev => ({
                      ...prev,
                      [comment.id]: e.target.value
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const text = replyText[comment.id] || '';
                      if (text.trim()) {
                        handleReply(comment.id);
                      }
                    }
                  }}
                />
                <div style={{ height: '-webkit-fill-available', display: 'flex', alignItems: 'flex-end' }}>
                  {(replyText[comment.id] || '').trim() && (
                    <button
                      className={styles.submitReplyButton}
                      onClick={() => handleReply(comment.id)}
                      disabled={submittingReply[comment.id]}
                    >
                      {submittingReply[comment.id] ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            style={{
                              animation: 'spin 0.8s linear infinite',
                              display: 'inline-block'
                            }}
                          >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="32" strokeDashoffset="32" opacity="0.3" fill="none"></circle>
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="32" strokeDashoffset="24" fill="none"></circle>
                          </svg>
                          Posting...
                        </span>
                      ) : (
                        'Comment'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Render Replies */}
          {comment.replies && comment.replies.length > 0 && visibleReplies.has(comment.id) && (
            <div className={styles.repliesContainer}>
              {comment.replies.map(reply => renderComment(reply, true))}
            </div>
          )}
          <div className={styles.updateTime}>
            {new Date(comment.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>
    );
  };

  const handleMarkAsDone = async () => {
    if (!taskId) return; // Only check taskId, groupId can be null for global tasks

    // Check if task requires documents and no files are selected
    if (taskProp?.requires_documents && selectedFiles.length === 0) {
      setShowDocumentPanel(true);
      return;
    }

    setMarkingAsDone(true);
    try {
      // Use submitTaskCompletion which handles both completion and document submission
      // For global tasks, groupId can be null
      const response = await apiService.submitTaskCompletion(taskId, groupId, {
        content: newComment,
        action_type: 'completion',
        attachments: selectedFiles,
        requires_documents: taskProp?.requires_documents || false
      });

      if (response.status === 'success') {
        notificationService.success('Task marked as completed successfully');
        setNewComment('');
        setSelectedUpdateAction('progress');
        setSelectedFiles([]);
        setShowActionDropdown(false);
        setShowAddUpdatePanel(false);

        // Reload task details to get updated status and comments
        refetchTaskDetails();

        // Reload user submissions to update submit button visibility
        if (!isAdmin) {
          loadUserSubmissions();
        }
      } else {
        notificationService.error(response.message || 'Failed to mark task as completed');
      }
    } catch (error: any) {
      console.error('Error marking task as done:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to mark task as completed');
    } finally {
      setMarkingAsDone(false);
    }
  };

  const handleStartProgress = async () => {
    if (!taskId) return;

    setStartingProgress(true);
    try {
      // Don't call addTaskComment here - the backend automatically creates a comment
      // when the assignment status changes to 'in_progress'
      // If user wants to add a custom comment, they can use the "Add Update" button separately
      // For global tasks, groupId can be null
      const response = await apiService.updateUserAssignmentStatus(taskId, groupId, {
        status: 'in_progress'
      });

      if (response.status === 'success') {
        notificationService.success('Started working on task');
        setNewComment('');
        setSelectedUpdateAction('progress');
        setSelectedFiles([]);
        setShowActionDropdown(false);
        setShowAddUpdatePanel(false);

        refetchTaskDetails();
      } else {
        notificationService.error(response.message || 'Failed to start progress');
      }
    } catch (error: any) {
      console.error('Error starting progress:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to start progress');
    } finally {
      setStartingProgress(false);
    }
  };

  const handleDocumentPanelConfirm = async () => {
    if (!taskId || selectedFiles.length === 0) {
      notificationService.error('Please select at least one document to proceed');
      return;
    }

    setMarkingAsDone(true);
    try {
      // Use submitTaskCompletion which handles both completion and document submission
      // For global tasks, groupId can be null
      const response = await apiService.submitTaskCompletion(taskId, groupId, {
        content: submissionNote,
        action_type: 'completion',
        attachments: selectedFiles,
        requires_documents: true
      });

      if (response.status === 'success') {
        notificationService.success('Task marked as completed successfully with proof of work');
        setNewComment('');
        setSubmissionNote('');
        setSelectedUpdateAction('progress');
        setSelectedFiles([]);
        setShowActionDropdown(false);
        setShowDocumentPanel(false);

        // Reload task details to get updated status and comments
        refetchTaskDetails();

        // Reload user submissions to update submit button visibility
        if (!isAdmin) {
          loadUserSubmissions();
        }
      } else {
        notificationService.error(response.message || 'Failed to mark task as completed');
      }
    } catch (error: any) {
      console.error('Error completing task with documents:', error);
      notificationService.error('Failed to mark task as completed');
    } finally {
      setMarkingAsDone(false);
    }
  };

  const handleDocumentPanelCancel = () => {
    setShowDocumentPanel(false);
    setPendingCompletion(false);
  };

  const handleAddUpdatePanelOpen = () => {
    setShowAddUpdatePanel(true);
  };

  const handleAddUpdatePanelClose = () => {
    setShowAddUpdatePanel(false);
    setNewComment('');
    setSelectedUpdateAction('progress');
    setSelectedFiles([]);
    setShowActionDropdown(false);
  };


  const getUpdateActionOptions = () => {
    return [
      {
        value: 'progress',
        label: 'Progress Update',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        ),
        color: '#3b82f6',
        description: 'Share your progress on this task'
      },
      {
        value: 'issue',
        label: 'Issue/Blocked',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        ),
        color: '#ef4444',
        description: 'Report an issue or blocker'
      },
      {
        value: 'question',
        label: 'Question',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        ),
        color: '#6366f1',
        description: 'Ask a question about the task'
      },
      {
        value: 'feedback',
        label: 'Feedback',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            <path d="M13 8H7"></path>
            <path d="M17 12H7"></path>
          </svg>
        ),
        color: '#06b6d4',
        description: 'Provide feedback or suggestions'
      },
      {
        value: 'general',
        label: 'General Update',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
        ),
        color: '#6b7280',
        description: 'General comment or update'
      },
    ];
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'not_started':
        return '#6b7280';
      case 'in_progress':
        return '#3b82f6';
      case 'completed':
        return '#10b981';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No deadline set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isInitialLoad) {
    return (
      <div className={styles.tabContent}>
        <SkeletonLoader count={5} height="40px" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className={styles.taskDetailsContainer}>
        <div className={styles.errorState}>
          <h2>Task not found</h2>
          <p>The task you're looking for doesn't exist or you don't have permission to view it.</p>
          <button
            className={styles.backButton}
            onClick={() => navigate(groupId ? `/group-management/groups/${groupId}/task-management` : '/task-management')}
          >
            Back to Task Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.updatesSection}>
      {/* Full Width Updates Container */}
      <div className={styles.updatesContainer}>
        <div className={styles.updatesHeader}>
          <div className={styles.updatesHeaderActions}>
            <div className={styles.updateCount}>
              {(() => {
                // Filter comments based on mode: 
                // - In personal mode, show only current user's comments UNLESS user is an approver
                // - If user is an approver in personal mode, show all comments
                let filteredComments = task?.comments || [];
                if (isPersonalMode && currentUser?.id) {
                  // Check if current user is an approver
                  const isApprover = task?.approvers?.some(approver => approver.user?.id === currentUser.id) ||
                    task?.approver_ids?.includes(currentUser.id);

                  // If user is an approver, show all comments; otherwise filter by user
                  if (!isApprover) {
                    filteredComments = filteredComments.filter((comment: TaskComment) =>
                      comment.created_by.id === currentUser.id
                    );
                  }
                }
                return filteredComments.length;
              })()} updates
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {/* Show Start Progress button if status is pending */}
              {isPersonalMode && currentUserAssignment?.status === 'pending' && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSuccess}`}
                  disabled={startingProgress}
                  onClick={handleStartProgress}
                >
                  {startingProgress ? 'Starting...' : 'Start Progress'}
                </button>
              )}

              {/* Show Submit Proof of Work button if status is in_progress and task requires documents */}
              {isPersonalMode && currentUserAssignment?.status === 'in_progress' && (taskProp?.requires_documents || task?.requires_documents) && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  disabled={submittingComment || markingAsDone}
                  onClick={() => {
                    setShowDocumentPanel(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Submit Proof of Work
                </button>
              )}

              {/* Show Mark as Done button if status is in_progress and task does NOT require documents */}
              {isPersonalMode && currentUserAssignment?.status === 'in_progress' && !(taskProp?.requires_documents || task?.requires_documents) && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSuccess}`}
                  disabled={markingAsDone || task?.status === 'completed'}
                  onClick={handleMarkAsDone}
                >
                  {markingAsDone ? 'Marking...' : (task?.status === 'completed' ? 'Completed' : 'Mark as Done')}
                </button>
              )}

              {/* Show Add Update button only in personal mode */}
              {isPersonalMode && (
                <button
                  className={buttonStyles.btn + ' ' + buttonStyles.btnPrimary}
                  onClick={handleAddUpdatePanelOpen}
                >
                  Add Update
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.updatesTimeline}>
          {(() => {
            console.log('Rendering comments. Task:', task);
            console.log('Comments array:', task?.comments);
            console.log('Comments length:', task?.comments?.length);

            // Filter comments based on mode: 
            // - In personal mode, show only current user's comments UNLESS user is an approver
            // - If user is an approver in personal mode, show all comments
            let filteredComments = task?.comments || [];
            if (isPersonalMode && currentUser?.id) {
              // Check if current user is an approver
              const isApprover = task?.approvers?.some(approver => approver.user?.id === currentUser.id) ||
                task?.approver_ids?.includes(currentUser.id);

              // If user is an approver, show all comments; otherwise filter by user
              if (!isApprover) {
                filteredComments = filteredComments.filter((comment: TaskComment) =>
                  comment.created_by.id === currentUser.id
                );
              }
            }

            if (filteredComments && filteredComments.length > 0) {
              console.log('Rendering comments list');
              return filteredComments.map((comment: TaskComment) => {
                console.log('Rendering comment:', comment);
                return renderComment(comment);
              });
            } else {
              console.log('Rendering empty state');
              return (
                <EmptyState
                  title="No Updates Yet"
                  description={
                    isPersonalMode
                      ? "You haven't shared any updates on this task yet."
                      : "Team members haven't shared any updates on this task yet."
                  }
                />
              );
            }
          })()}
        </div>
      </div>

      {/* Document Upload SidePanel */}
      <SidePanel
        isOpen={showDocumentPanel}
        onClose={handleDocumentPanelCancel}
        title="Proof of Work Required"
        icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10,9 9,9 8,9" />
          </svg>
        }
      >
        <div className={sidePanelStyles.form}>
          <div className={sidePanelStyles.formGroup}>
            {/* Header Section */}
            <div className={sidePanelStyles.sectionHeader}>
              <p className={sidePanelStyles.emptyDescription}>
                This task requires proof of work to be uploaded before marking as completed.
                Please upload your documents to proceed.
              </p>
            </div>

            {/* Submission Note */}
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>Submission Note (optional)</label>
              <textarea
                className={styles.updateTextarea}
                placeholder="Add context about your submission..."
                rows={3}
                value={submissionNote}
                onChange={(e) => setSubmissionNote(e.target.value)}
              />
            </div>

            {/* File Upload Section */}
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Attach Documents
              </label>
              <div
                className={`${styles.fileUploadArea} ${isDragOver ? styles.dragover : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  id="documentUpload"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.ppt,.pptx"
                  onChange={handleFileSelect}
                  className={styles.fileInput}
                />
                <label htmlFor="documentUpload" className={styles.fileUploadLabel}>
                  <div className={styles.fileUploadIcon}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className={styles.fileUploadText}>
                    <strong>Drag & drop your files here</strong>
                    <span>
                      or <span className={styles.browseLink}>browse</span> to select files
                    </span>
                    <div className={styles.acceptedFileTypes}>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('pdf', 'document.pdf')}</span>
                        <span>.pdf</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('word', 'document.doc')}</span>
                        <span>.doc</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('word', 'document.docx')}</span>
                        <span>.docx</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('text', 'document.txt')}</span>
                        <span>.txt</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('image', 'image.jpg')}</span>
                        <span>.jpg</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('image', 'image.jpeg')}</span>
                        <span>.jpeg</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('image', 'image.png')}</span>
                        <span>.png</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('image', 'image.gif')}</span>
                        <span>.gif</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('excel', 'spreadsheet.xlsx')}</span>
                        <span>.xlsx</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('excel', 'spreadsheet.xls')}</span>
                        <span>.xls</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('powerpoint', 'presentation.ppt')}</span>
                        <span>.ppt</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('powerpoint', 'presentation.pptx')}</span>
                        <span>.pptx</span>
                      </div>
                    </div>
                    <small>
                      Max 50MB each • Security scanning enabled
                    </small>
                  </div>
                </label>
              </div>
            </div>

            {/* Selected Files Display */}
            {selectedFiles.length > 0 && (
              <div className={sidePanelStyles.formGroup}>
                <h4 className={styles.uploadedFilesTitle}>Uploaded Files ({selectedFiles.length})</h4>
                <div className={styles.uploadedFiles}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className={styles.uploadedFileItem}>
                      <div className={styles.fileInfo}>
                        <div className={styles.fileIcon}>
                          {getFileIcon(file.type, file.name)}
                        </div>
                        <div className={styles.fileDetails}>
                          <div className={styles.fileName}>{file.name}</div>
                          <div className={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className={styles.removeFileBtn}
                          title="Remove file"
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className={sidePanelStyles.formActions}>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={handleDocumentPanelCancel}
              >
                Cancel
              </button>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={handleDocumentPanelConfirm}
                disabled={markingAsDone || selectedFiles.length === 0}
              >
                {markingAsDone ? 'Marking as Done...' : 'Mark as Done'}
              </button>
            </div>
          </div>
        </div>
      </SidePanel>

      {/* Add Update SidePanel */}
      <SidePanel
        isOpen={showAddUpdatePanel}
        onClose={handleAddUpdatePanelClose}
        title="Add Update"

      >
        <div className={sidePanelStyles.form}>
          <div className={sidePanelStyles.formGroup} style={{ marginTop: '0' }}>
            {/* Update Action Selector */}
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>Update Type <span className={sidePanelStyles.required}>*</span></label>
              <div className={styles.actionDropdown} ref={actionDropdownRef}>
                <button
                  className={styles.actionDropdownToggle}
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <span className={styles.actionIcon}>
                    {selectedUpdateAction ?
                      getUpdateActionOptions().find(action => action.value === selectedUpdateAction)?.icon || (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14,2 14,8 20,8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10,9 9,9 8,9" />
                        </svg>
                      )
                      : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14,2 14,8 20,8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10,9 9,9 8,9" />
                        </svg>
                      )
                    }
                  </span>
                  <span className={styles.actionLabel}>
                    {selectedUpdateAction ?
                      getUpdateActionOptions().find(action => action.value === selectedUpdateAction)?.label || 'Select Action'
                      : 'Select Update Type'
                    }
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6,9 12,15 18,9" />
                  </svg>
                </button>

                {showActionDropdown && (
                  <div className={styles.actionDropdownMenu}>
                    {getUpdateActionOptions().map((action) => (
                      <button
                        key={action.value}
                        className={styles.actionOption}
                        onClick={() => {
                          setSelectedUpdateAction(action.value);
                          setShowActionDropdown(false);
                        }}
                        style={{ '--action-color': action.color } as React.CSSProperties}
                      >
                        <span className={styles.actionOptionIcon}>{action.icon}</span>
                        <div className={styles.actionOptionContent}>
                          <span className={styles.actionOptionLabel}>{action.label}</span>
                          <span className={styles.actionOptionDescription}>{action.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Comment Textarea */}
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>Update Message <span className={sidePanelStyles.required}>*</span></label>
              <textarea
                className={sidePanelStyles.formControl}
                placeholder="Share your progress or ask a question..."
                rows={4}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
            </div>

            {/* Action Buttons */}
            <div className={sidePanelStyles.formActions}>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={handleAddUpdatePanelClose}
              >
                Cancel
              </button>
              <div className={styles.actionButtons}>
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  disabled={!newComment.trim() || submittingComment}
                  onClick={handleAddComment}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22,2 15,22 11,13 2,9 22,2" />
                  </svg>
                  {submittingComment ? 'Posting...' : 'Post Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </SidePanel>

    </div>
  );
};

export default TaskUpdates;